import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../services/api';
import NycemLogo from '../components/NycemLogo';

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const { login } = useAuth();

  // Step 1 — credentials
  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');

  // Step 2 — set new password (NEW_PASSWORD_REQUIRED challenge)
  const [step, setStep]                   = useState<'login' | 'set-password' | 'system-asleep'>('login');
  // The prototype database is kept stopped to save cost, so a successful Cognito sign-in
  // can land on a system whose every API call fails. These drive the wake screen.
  const [dbStatus, setDbStatus]           = useState<string>('');
  const [canWake, setCanWake]             = useState(false);
  const [waking, setWaking]               = useState(false);
  const [completer, setCompleter]         = useState<((pw: string) => Promise<void>) | null>(null);
  const [newPassword, setNewPassword]     = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const [error, setError]     = useState('');
  const [loading, setLoading] = useState(false);

  // ── Step 1: sign in with email + temp/current password ──────────────────────
  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const result = await login(email, password);
      if (result.type === 'success') {
        // Cognito is a separate service from the database, so sign-in succeeds even while
        // the instance is stopped — and then every authenticated request fails. Ask before
        // navigating, so the user gets an explanation and a way out instead of a dashboard
        // full of errors.
        if (!(await systemIsAwake())) return;
        navigate('/dashboard', { replace: true });
      } else {
        // NEW_PASSWORD_REQUIRED — show the set-password form
        setCompleter(() => result.complete);
        setStep('set-password');
        setLoading(false);
      }
    } catch (err: unknown) {
      setLoading(false);
      setError((err as Error)?.message || 'Authentication failed. Please check your credentials.');
    }
  };

  /**
   * Returns true when the API is usable. When it is not, switches to the wake screen and
   * returns false so the caller does not navigate.
   *
   * Deliberately fails OPEN: if /system/status itself cannot be reached we let the user
   * through rather than trapping them behind a wake screen for an unrelated outage. A
   * dashboard showing errors is recoverable; a login page that refuses to proceed is not.
   */
  const systemIsAwake = async (): Promise<boolean> => {
    try {
      const res = await api.getSystemStatus();
      const d = res?.data ?? res;
      if (d?.awake) return true;
      setDbStatus(String(d?.database_status ?? 'unknown'));
      setCanWake(Boolean(d?.can_wake));
      setStep('system-asleep');
      setLoading(false);
      // Already on its way up — someone pressed start, possibly this user before they
      // navigated away. `waking` is component state and is lost on remount, so without
      // this the screen offered "Start the system" for something already starting, and a
      // second press just reported "already starting" with no progress shown.
      if (d?.transitional) {
        setWaking(true);
        pollUntilAwake();
      }
      return false;
    } catch {
      return true;
    }
  };

  /**
   * Poll until the database is available, then continue to the dashboard.
   *
   * Shared by the Start button and by arriving on this screen to find a startup already
   * in progress. Capped at ~10 minutes: past that something is wrong, and a spinner
   * forever is worse than a message.
   */
  const pollUntilAwake = () => {
    const deadline = Date.now() + 10 * 60_000;
    const poll = async () => {
      try {
        const res = await api.getSystemStatus();
        const d = res?.data ?? res;
        setDbStatus(String(d?.database_status ?? 'unknown'));
        if (d?.awake) { navigate('/dashboard', { replace: true }); return; }
      } catch { /* keep polling — transient errors are expected during startup */ }
      if (Date.now() > deadline) {
        setWaking(false);
        setError('The system is taking longer than expected to start. Please try again or contact your administrator.');
        return;
      }
      setTimeout(poll, 15_000);
    };
    setTimeout(poll, 15_000);
  };

  const handleWake = async () => {
    setError('');
    setWaking(true);
    try {
      await api.wakeSystem();
    } catch (err: unknown) {
      setWaking(false);
      setError((err as Error)?.message || 'Could not start the system.');
      return;
    }
    pollUntilAwake();
  };

  // ── Step 2: complete the new-password challenge ──────────────────────────────
  const handleSetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    setError('');
    setLoading(true);
    try {
      await completer!(newPassword);
      navigate('/dashboard', { replace: true });
    } catch (err: unknown) {
      setLoading(false);
      setError((err as Error)?.message || 'Failed to set password. Please try again.');
    }
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        // NYCEM Blue band behind the card's top, matching the brand guide's slide pattern.
        background: 'linear-gradient(180deg, var(--nycem-blue) 0 38vh, var(--bg) 38vh)',
        padding: '24px 16px',
        paddingTop: 60,
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: 380,
          background: 'var(--white)',
          borderRadius: 8,
          padding: 36,
          borderTop: '4px solid var(--hazard)',
          boxShadow: '0 8px 32px rgba(34, 43, 78, 0.18)',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 18 }}>
          <NycemLogo variant="black" width={260} />
        </div>

        <h1
          style={{
            fontSize: 22,
            fontWeight: 900,
            color: 'var(--heading)',
            textAlign: 'center',
            marginBottom: 4,
          }}
        >
          Interagency Directory
        </h1>
        <p
          style={{
            fontSize: 14,
            color: 'var(--gray-500)',
            textAlign: 'center',
            marginBottom: 28,
          }}
        >
          {step === 'set-password' ? 'Set your new password'
            : step === 'system-asleep' ? 'System is asleep'
            : 'Sign in to continue'}
        </p>

        {/* ── System asleep: the database is stopped to save cost ──────────── */}
        {step === 'system-asleep' && (
          <div>
            <p style={{ marginBottom: 12, lineHeight: 1.5 }}>
              {waking
                ? 'The system is starting. This takes about five minutes from a cold start.'
                : 'You are signed in, but the system database is currently stopped to reduce running costs while the prototype is idle.'}
            </p>
            {canWake ? (
              <>
                {!waking && (
                  <p style={{ marginBottom: 16, lineHeight: 1.5 }}>
                    You can start it now. It takes about five minutes, and it will stop
                    itself again overnight.
                  </p>
                )}
                <button
                  type="button"
                  className="btn btn-primary"
                  style={{ width: '100%' }}
                  onClick={handleWake}
                  disabled={waking}
                >
                  {waking ? 'Starting the system…' : 'Start the system'}
                </button>
                {waking && (
                  <p aria-live="polite" style={{ marginTop: 12, fontSize: 14, opacity: 0.8 }}>
                    Status: {dbStatus || 'starting'} — this page will continue automatically
                    once the system is ready. You can leave this tab open.
                  </p>
                )}
              </>
            ) : (
              <p style={{ marginBottom: 16, lineHeight: 1.5 }}>
                Starting it requires an administrator. Please ask your IAD contact to
                start the system, then sign in again.
              </p>
            )}
            {!waking && (
              <button
                type="button"
                className="btn"
                style={{ width: '100%', marginTop: 12 }}
                onClick={() => { setStep('login'); setError(''); }}
              >
                Back
              </button>
            )}
          </div>
        )}

        {/* ── Step 1: credentials ─────────────────────────────────────────── */}
        {step === 'login' && (
          <form onSubmit={handleLogin} noValidate>
            <div style={{ marginBottom: 16 }}>
              <label htmlFor="email" className="field-label">Email Address</label>
              <input
                id="email"
                type="email"
                className="field-input"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="you@example.com"
                autoComplete="email"
                required
                disabled={loading}
              />
            </div>
            <div style={{ marginBottom: 0 }}>
              <label htmlFor="password" className="field-label">Password</label>
              <input
                id="password"
                type="password"
                className="field-input"
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder="••••••••"
                autoComplete="current-password"
                required
                disabled={loading}
              />
            </div>

            {error && (
              <p style={{ marginTop: 8, fontSize: 12, color: 'var(--red)', lineHeight: 1.4 }}>
                {error}
              </p>
            )}

            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading || !email || !password}
              style={{
                width: '100%', height: 40, justifyContent: 'center',
                fontSize: 14, fontWeight: 600, marginTop: 20, marginBottom: 12,
              }}
            >
              {loading ? 'Signing in…' : 'Sign In'}
            </button>
          </form>
        )}

        {/* ── Step 2: set new password ─────────────────────────────────────── */}
        {step === 'set-password' && (
          <>
            <div
              style={{
                background: 'var(--green-50)', border: '1px solid var(--green-50)',
                borderRadius: 6, padding: '10px 14px', marginBottom: 20, fontSize: 14,
                color: 'var(--green)', lineHeight: 1.5,
              }}
            >
              Your temporary password was accepted. Please set a permanent password to continue.
            </div>
            <form onSubmit={handleSetPassword} noValidate>
              <div style={{ marginBottom: 16 }}>
                <label htmlFor="new-password" className="field-label">New Password</label>
                <input
                  id="new-password"
                  type="password"
                  className="field-input"
                  value={newPassword}
                  onChange={e => setNewPassword(e.target.value)}
                  placeholder="Min 12 chars, upper, lower, number, symbol"
                  autoComplete="new-password"
                  required
                  disabled={loading}
                />
              </div>
              <div style={{ marginBottom: 0 }}>
                <label htmlFor="confirm-password" className="field-label">Confirm Password</label>
                <input
                  id="confirm-password"
                  type="password"
                  className="field-input"
                  value={confirmPassword}
                  onChange={e => setConfirmPassword(e.target.value)}
                  placeholder="••••••••"
                  autoComplete="new-password"
                  required
                  disabled={loading}
                />
              </div>

              {error && (
                <p style={{ marginTop: 8, fontSize: 12, color: 'var(--red)', lineHeight: 1.4 }}>
                  {error}
                </p>
              )}

              <button
                type="submit"
                className="btn btn-primary"
                disabled={loading || !newPassword || !confirmPassword}
                style={{
                  width: '100%', height: 40, justifyContent: 'center',
                  fontSize: 14, fontWeight: 600, marginTop: 20, marginBottom: 12,
                }}
              >
                {loading ? 'Setting password…' : 'Set Password & Sign In'}
              </button>
            </form>
          </>
        )}

        {/* Forgot password link — only shown on step 1 */}
        {step === 'login' && (
          <div style={{ textAlign: 'center', marginBottom: 20 }}>
            <span
              style={{ fontSize: 12, color: 'var(--gray-500)', cursor: 'pointer' }}
              onClick={() => setError('Password reset: contact your administrator.')}
            >
              Forgot password?
            </span>
          </div>
        )}

        <hr style={{ border: 'none', borderTop: '1px solid var(--gray-200)', marginBottom: 16 }} />

        <div
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            gap: 5, color: 'var(--gray-500)', fontSize: 12,
          }}
        >
          <Lock size={12} />
          <span style={{ textTransform: 'uppercase', letterSpacing: '0.04em' }}>
            Authorized Access Only · NYC Emergency Management
          </span>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
